import React from "react";
import { collection, doc, getDoc, getDocs, limit, orderBy, query, where } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { db, functions } from "../firebase";
import { useAuth } from "./AuthContext";
import ReviewStars from "./ReviewStars";

type ReviewDoc = {
  id: string;
  authorName?: string;
  rating?: number;
  comment?: string;
  createdAt?: { toDate?: () => Date };
  appointmentId?: string;
};

type EligibleAppointment = {
  id: string;
  dataSessao?: any;
};

function formatReviewDate(value?: { toDate?: () => Date }): string {
  const date = value?.toDate?.();
  if (!date) return "";
  return date.toLocaleDateString("pt-BR");
}

export default function TherapistReviews({
  therapistId,
  reviewAverage = 0,
  reviewCount = 0,
}: {
  therapistId: string;
  reviewAverage?: number;
  reviewCount?: number;
}) {
  const { user, isAuthenticated, isProfileComplete } = useAuth();
  const [reviews, setReviews] = React.useState<ReviewDoc[]>([]);
  const [alreadyReviewed, setAlreadyReviewed] = React.useState(false);
  const [eligible, setEligible] = React.useState<EligibleAppointment[]>([]);
  const [appointmentId, setAppointmentId] = React.useState("");
  const [rating, setRating] = React.useState(5);
  const [comment, setComment] = React.useState("");
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [success, setSuccess] = React.useState<string | null>(null);

  React.useEffect(() => {
    let cancelled = false;
    const load = async () => {
      const reviewsQuery = query(
        collection(db, "therapistReviews"),
        where("therapistId", "==", therapistId),
        orderBy("createdAt", "desc"),
        limit(20),
      );
      const snapshot = await getDocs(reviewsQuery);
      if (cancelled) return;
      setReviews(snapshot.docs.map((docSnap) => ({ id: docSnap.id, ...(docSnap.data() as Omit<ReviewDoc, "id">) })));
    };
    load().catch((err) => {
      console.error("Erro ao carregar avaliações:", err);
    });
    return () => {
      cancelled = true;
    };
  }, [therapistId]);

  React.useEffect(() => {
    if (!isAuthenticated || !isProfileComplete || !user?.uid) {
      setEligible([]);
      setAlreadyReviewed(false);
      return;
    }
    let cancelled = false;
    const loadEligible = async () => {
      const ownReview = await getDoc(doc(db, "therapistReviews", `${therapistId}_${user.uid}`));
      if (cancelled) return;
      if (ownReview.exists()) {
        setAlreadyReviewed(true);
        setEligible([]);
        return;
      }
      setAlreadyReviewed(false);

      const appointmentsQuery = query(
        collection(db, "appointments"),
        where("pacienteId", "==", user.uid),
        limit(50),
      );
      const snapshot = await getDocs(appointmentsQuery);
      const now = Date.now();
      const next = snapshot.docs
        .map((docSnap) => ({ id: docSnap.id, ...(docSnap.data() as any) }))
        .filter((item) => {
          if (item.terapeutaId !== therapistId) return false;
          if (item.status === "cancelado") return false;
          const session = item.dataSessao?.toDate?.() || (item.dataSessao ? new Date(item.dataSessao) : null);
          return session instanceof Date && session.getTime() <= now;
        })
        .sort((a, b) => {
          const aTime = a.dataSessao?.toDate?.()?.getTime?.() || 0;
          const bTime = b.dataSessao?.toDate?.()?.getTime?.() || 0;
          return bTime - aTime;
        });
      if (!cancelled) {
        setEligible(next);
        if (next[0]?.id) setAppointmentId(next[0].id);
      }
    };
    loadEligible().catch((err) => {
      console.error("Erro ao buscar sessões para avaliar:", err);
    });
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, isProfileComplete, user?.uid, therapistId]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const createReview = httpsCallable(functions, "createTherapistReview");
      await createReview({ therapistId, appointmentId, rating, comment });
      setSuccess("Avaliação publicada. Obrigado!");
      setComment("");
      setAlreadyReviewed(true);
      setEligible([]);
      setReviews((prev) => [
        {
          id: `${therapistId}_${user?.uid}`,
          authorName: user?.name || "Você",
          rating,
          comment,
          createdAt: { toDate: () => new Date() },
          appointmentId,
        },
        ...prev,
      ]);
    } catch (err: any) {
      setError(err?.details || err?.message || "Não foi possível enviar a avaliação.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="mt-8 rounded-2xl bg-white p-6 shadow-md">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-2xl font-semibold" style={{ color: "#6C4E78" }}>
          Avaliações
        </h2>
        <div className="flex items-center gap-2">
          <ReviewStars value={reviewAverage} />
          <span className="text-sm text-[#4A7C75]">
            {reviewCount > 0 ? `${reviewAverage.toFixed(1)} · ${reviewCount} avaliação${reviewCount === 1 ? "" : "ões"}` : "Ainda sem avaliações"}
          </span>
        </div>
      </div>

      {isAuthenticated && isProfileComplete && eligible.length > 0 && !alreadyReviewed && (
        <form onSubmit={handleSubmit} className="mb-8 rounded-xl border border-[#D5E1DB] bg-[#F7FBF9] p-4">
          <p className="mb-3 text-sm font-medium text-[#2E4B4C]">Avalie sua sessão com este constelador</p>
          <p className="mb-3 text-xs text-[#5A7272]">Uma avaliação por pessoa. Só quem fez a constelação pode avaliar.</p>
          <div className="mb-3">
            <ReviewStars value={rating} onChange={setRating} label="Sua nota" />
          </div>
          <textarea
            required
            minLength={8}
            maxLength={500}
            value={comment}
            onChange={(event) => setComment(event.target.value)}
            placeholder="Como foi a sessão? Escreva um comentário sincero."
            className="mb-3 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
            rows={4}
          />
          {error && <p className="mb-2 text-sm text-red-700">{error}</p>}
          {success && <p className="mb-2 text-sm text-green-700">{success}</p>}
          <button
            type="submit"
            disabled={saving || !appointmentId}
            className="rounded-full bg-[#4A7C75] px-5 py-2 text-sm font-semibold text-white hover:bg-[#406b65] disabled:opacity-60"
          >
            {saving ? "Enviando..." : "Publicar avaliação"}
          </button>
        </form>
      )}

      {reviews.length === 0 ? (
        <p className="text-sm text-[#5A7272]">As primeiras avaliações deste constelador aparecem aqui.</p>
      ) : (
        <ul className="space-y-4">
          {reviews.map((review) => (
            <li key={review.id} className="rounded-xl border border-[#E8EFEB] p-4">
              <div className="mb-2 flex items-center justify-between gap-3">
                <p className="font-semibold text-[#6C4E78]">{review.authorName || "Paciente"}</p>
                <span className="text-xs text-[#85A7A8]">{formatReviewDate(review.createdAt)}</span>
              </div>
              <ReviewStars value={Number(review.rating) || 0} size="sm" />
              <p className="mt-2 whitespace-pre-line text-sm leading-6 text-[#4A7C75]">{review.comment}</p>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
